import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ChevronDown,
  ChevronLeft,
  LoaderCircle,
  Minus,
  Plus,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { CasegoodsInventoryItem, ModularWorkstations, OrgInventoryItem } from '../../types/admin';

// ── Inventory computation helpers ─────────────────────────────────────────────

/**
 * Compute the scaled quantity for each modular component.
 * qty = ceil(quantitySampled * totalWorkstations / workstationsSampled)
 */
function computeModularComponents(
  modularItems: OrgInventoryItem[],
  samplesMap: Record<string, number>,
  totalWorkstations: number,
  workstationsSampled: number,
): CasegoodsInventoryItem[] {
  const result: CasegoodsInventoryItem[] = [];
  for (const item of modularItems) {
    const sampled = samplesMap[item.id] ?? 0;
    if (sampled === 0) continue;
    const qty = Math.ceil((sampled * totalWorkstations) / workstationsSampled);
    result.push({ itemId: item.id, itemName: item.name, quantity: qty });
  }
  return result;
}

/**
 * For each modular component's addEquipment entries, aggregate total equipment needed
 * based on the component's scaled quantity.
 */
function computeModularEquipment(
  modularItems: OrgInventoryItem[],
  computedComponents: CasegoodsInventoryItem[],
): CasegoodsInventoryItem[] {
  const compMap = new Map(computedComponents.map(c => [c.itemId, c.quantity]));
  const equipMap = new Map<string, CasegoodsInventoryItem>();

  for (const item of modularItems) {
    const compQty = compMap.get(item.id) ?? 0;
    if (compQty === 0 || !item.addEquipment?.length) continue;

    for (const eq of item.addEquipment) {
      const eqQty = Math.ceil(eq.quantity * compQty);
      const existing = equipMap.get(eq.itemId);
      if (existing) {
        equipMap.set(eq.itemId, { ...existing, quantity: existing.quantity + eqQty });
      } else {
        equipMap.set(eq.itemId, { itemId: eq.itemId, itemName: eq.itemName, quantity: eqQty });
      }
    }
  }
  return Array.from(equipMap.values());
}

/**
 * Replace all modular-contributed items in the existing casegoods inventory
 * with the newly computed modular items. Non-modular items are preserved.
 */
function updateInventoryWithModular(
  currentInventory: CasegoodsInventoryItem[],
  modularComponents: CasegoodsInventoryItem[],
  modularEquipment: CasegoodsInventoryItem[],
  modularItems: OrgInventoryItem[],
): CasegoodsInventoryItem[] {
  // Build set of all item IDs that modular workstations can contribute
  const modularIds = new Set<string>();
  for (const item of modularItems) {
    modularIds.add(item.id);
    for (const eq of item.addEquipment ?? []) modularIds.add(eq.itemId);
  }

  const nonModular = currentInventory.filter(i => !modularIds.has(i.itemId));
  return [...nonModular, ...modularComponents, ...modularEquipment];
}

// ── Counter component ─────────────────────────────────────────────────────────

interface CounterProps {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  id?: string;
}

function Counter({ value, onChange, min = 0, id }: CounterProps) {
  const [raw, setRaw] = useState(String(value));
  const [editing, setEditing] = useState(false);

  if (!editing && raw !== String(value)) {
    setRaw(String(value));
  }

  return (
    <div className="flex items-center gap-1 shrink-0">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-9 h-9 flex items-center justify-center rounded-lg border border-stone
                   text-slate hover:text-midnight hover:border-plum/40 active:bg-mist
                   transition-colors duration-100"
        aria-label="Decrease"
      >
        <Minus size={15} />
      </button>

      <input
        id={id}
        type="number"
        min={min}
        step="1"
        value={raw}
        onFocus={() => setEditing(true)}
        onChange={e => setRaw(e.target.value)}
        onBlur={() => {
          const n = parseInt(raw, 10);
          if (isNaN(n) || n < min) setRaw(String(value));
          else onChange(n);
          setEditing(false);
        }}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="w-14 h-9 text-center rounded-lg border border-stone bg-white
                   text-small font-semibold text-midnight
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors duration-150"
        aria-label="Value"
      />

      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="w-9 h-9 flex items-center justify-center rounded-lg border border-stone
                   text-slate hover:text-midnight hover:border-plum/40 active:bg-mist
                   transition-colors duration-100"
        aria-label="Increase"
      >
        <Plus size={15} />
      </button>
    </div>
  );
}

// ── Toggle row ────────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className="shrink-0 mt-0.5"
        aria-label={`Toggle ${label}`}
      >
        {value
          ? <ToggleRight size={26} className="text-plum" />
          : <ToggleLeft size={26} className="text-slate" />
        }
      </button>
      <div>
        <p className="text-small font-medium text-midnight">{label}</p>
        {description && <p className="text-[12px] text-slate mt-0.5 leading-snug">{description}</p>}
      </div>
    </div>
  );
}

// ── Card components ───────────────────────────────────────────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone">
        <p className="font-semibold text-small text-midnight">{title}</p>
      </div>
      <div className="px-4 py-4 flex flex-col gap-4">
        {children}
      </div>
    </div>
  );
}

function CollapsibleCard({
  title,
  badge,
  children,
  defaultOpen = false,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 justify-between px-4 py-3.5
                   hover:bg-mist active:bg-stone transition-colors duration-150
                   pointer-events-auto"
      >
        <div className="flex items-center gap-2">
          <span className="font-semibold text-small text-midnight">{title}</span>
          {badge && (
            <span className="text-[11px] font-semibold text-white bg-plum px-1.5 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`text-slate shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-t border-stone">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Sampling Card ─────────────────────────────────────────────────────────────

function SamplingCard({
  displayedModularItems,
  samplesMap,
  setSamplesMap,
  totalWorkstations,
  workstationsSampled,
  setWorkstationsSampled,
  modularWorkstationItem,
}: {
  displayedModularItems: OrgInventoryItem[];
  samplesMap: Record<string, number>;
  setSamplesMap: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  totalWorkstations: number;
  workstationsSampled: number;
  setWorkstationsSampled: (n: number) => void;
  modularWorkstationItem: OrgInventoryItem | undefined;
}) {
  return (
    <Card title="Workstation Sampling">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-small font-medium text-midnight">Workstations sampled</p>
          <p className="text-[12px] text-slate mt-0.5">How many workstations were inventoried</p>
        </div>
        <Counter
          value={workstationsSampled}
          min={1}
          onChange={n => {
            setWorkstationsSampled(n);
            if (modularWorkstationItem) {
              setSamplesMap(prev => ({ ...prev, [modularWorkstationItem.id]: n }));
            }
          }}
        />
      </div>

      {displayedModularItems.length === 0 ? (
        <div className="flex flex-col items-center py-8 gap-2 text-center">
          <p className="text-small text-slate">
            No modular component items in your inventory yet.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-0 -mx-4">
          <div className="px-4 pb-2 flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
              Components
            </p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
              Qty Sampled
            </p>
          </div>
          <ul className="flex flex-col divide-y divide-stone">
            {displayedModularItems.map(item => {
              const sampledQty = samplesMap[item.id] ?? 0;
              const scaledQty = sampledQty > 0
                ? Math.ceil((sampledQty * totalWorkstations) / workstationsSampled)
                : 0;
              return (
                <li key={item.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-small font-medium text-midnight truncate">{item.name}</p>
                    {scaledQty > 0 && (
                      <p className="text-[11px] text-slate mt-0.5">
                        → {scaledQty} total
                      </p>
                    )}
                  </div>
                  <Counter
                    value={sampledQty}
                    min={0}
                    onChange={n => setSamplesMap(prev => ({ ...prev, [item.id]: n }))}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ── Equipment Card ───────────────────────────────────────────────────────────

function EquipmentCard({
  equipmentItems,
  defaultOpen,
}: {
  equipmentItems: CasegoodsInventoryItem[];
  defaultOpen: boolean;
}) {
  const hasItems = equipmentItems.length > 0;
  return (
    <CollapsibleCard
      title="Equipment"
      badge={hasItems ? String(equipmentItems.length) : undefined}
      defaultOpen={defaultOpen}
    >
      {hasItems ? (
        <ul className="flex flex-col divide-y divide-stone">
          {equipmentItems.map(eq => (
            <li key={eq.itemId} className="flex items-center justify-between gap-3 px-4 py-3">
              <p className="text-small font-medium text-midnight truncate flex-1 min-w-0">{eq.itemName}</p>
              <span className="shrink-0 text-small font-semibold text-midnight tabular-nums">
                {eq.quantity}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-4 py-6 text-center">
          <p className="text-small text-slate">
            No auto-added equipment yet. Equipment is added automatically based on sampled component quantities.
          </p>
        </div>
      )}
    </CollapsibleCard>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ModularWorkstationsPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { quote, loading: quoteLoading, updateQuote, orgItems, orgItemsLoading: inventoryLoading } = useSharedQuote();

  const [initialized, setInitialized] = useState(false);
  const justInitialized = useRef(false);

  // Workstation settings
  const [totalWorkstations, setTotalWorkstations] = useState(0);
  const [moverDisassembling, setMoverDisassembling] = useState(false);
  const [moverReassembling, setMoverReassembling] = useState(false);

  // Sampling
  const [workstationsSampled, setWorkstationsSampled] = useState(4);
  const [samplesMap, setSamplesMap] = useState<Record<string, number>>({});

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derived: Modular Component items alphabetically sorted
  const modularItems = orgItems
    .filter(i => i.type === 'Modular Component')
    .sort((a, b) => a.name.localeCompare(b.name));

  // "Modular Workstation" is a special inventory item whose quantitySampled is
  // always kept in sync with workstationsSampled.  It participates in equipment
  // calculation but is hidden from the user-editable Components list.
  const modularWorkstationItem = modularItems.find(i => i.name === 'Modular Workstation');
  // Customer items → Workstation Sampling card
  const displayedModularItems = modularItems.filter(
    i => i.name !== 'Modular Workstation' && (!i.residentialType || i.residentialType === 'customer_item'),
  );
  // Initialize once both quote and org inventory are loaded
  useEffect(() => {
    if (quote && !inventoryLoading && !initialized) {
      justInitialized.current = true;
      const saved = quote.modularWorkstations;
      setTotalWorkstations(saved?.totalWorkstations ?? 0);
      setMoverDisassembling(saved?.moverDisassembling ?? false);
      setMoverReassembling(saved?.moverReassembling ?? false);
      setWorkstationsSampled(saved?.workstationsSampled ?? 4);

      const wsSampled = saved?.workstationsSampled ?? 4;
      const map: Record<string, number> = {};
      for (const item of modularItems) {
        const s = saved?.samples.find(x => x.itemId === item.id);
        map[item.id] = s?.quantitySampled ?? 0;
      }
      // Keep "Modular Workstation" in sync with workstationsSampled
      const wsItem = modularItems.find(i => i.name === 'Modular Workstation');
      if (wsItem) map[wsItem.id] = wsSampled;
      setSamplesMap(map);
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, inventoryLoading, initialized]);

  // Compute modular contribution from current state (used in save + Equipment card display)
  const computeContribution = useCallback(() => {
    const components = computeModularComponents(modularItems, samplesMap, totalWorkstations, workstationsSampled);
    const equipment = computeModularEquipment(modularItems, components);
    return { components, equipment };
  }, [modularItems, samplesMap, totalWorkstations, workstationsSampled]);

  // Build modularWorkstations payload
  const buildModularPayload = useCallback((): ModularWorkstations => ({
    totalWorkstations,
    moverDisassembling,
    moverReassembling,
    workstationsSampled,
    samples: Object.entries(samplesMap)
      .filter(([, qty]) => qty > 0)
      .map(([itemId, quantitySampled]) => ({
        itemId,
        itemName: orgItems.find(i => i.id === itemId)?.name ?? '',
        quantitySampled,
      })),
  }), [totalWorkstations, moverDisassembling, moverReassembling, workstationsSampled, samplesMap, orgItems]);

  // Debounced auto-save: persists modular settings AND updates casegoodsInventory
  useEffect(() => {
    if (!initialized) return;
    if (justInitialized.current) {
      justInitialized.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (quote?.quoteStatus !== 'draft') return;
      setSaving(true);
      setSaveError('');
      const { components, equipment } = computeContribution();
      const newInventory = updateInventoryWithModular(
        quote?.casegoodsInventory ?? [],
        components,
        equipment,
        modularItems,
      );
      updateQuote({
        modularWorkstations: buildModularPayload(),
        casegoodsInventory: newInventory,
      })
        .then(() => setSaving(false))
        .catch(err => {
          setSaveError(err instanceof Error ? err.message : 'Auto-save failed');
          setSaving(false);
        });
    }, 400);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [initialized, computeContribution, buildModularPayload, quote, modularItems, updateQuote]);

  const loading = quoteLoading || (inventoryLoading && !initialized);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  // Compute scaled components + auto-added equipment from addEquipment entries
  const { components: displayComponents, equipment: computedEquipment } = computeContribution();

  return (
    <div className="px-4 pt-6 pb-10 md:max-w-5xl md:mx-auto">
      {/* Back — hidden on md+ (shell left panel provides navigation) */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quoteId}`)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">Modular Workstations</h1>
        {saving && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
      </div>

      {quote.quoteStatus !== 'draft' && <ReadOnlyBanner status={quote.quoteStatus} />}

      {saveError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      {/*
        Mobile:  single column — Settings → Sampling → Equipment
        md+:     left column (Settings + Equipment), right column (Sampling)
      */}
      <div className={quote.quoteStatus !== 'draft' ? 'pointer-events-none opacity-60 select-none' : ''}>
        {/* Mobile layout */}
        <div className="md:hidden space-y-3">
          {/* ── Workstation Settings card ── */}
          <Card title="Modular Workstations">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-small font-medium text-midnight">Total Workstations</p>
                <p className="text-[12px] text-slate mt-0.5">Number of workstations being moved</p>
              </div>
              <Counter value={totalWorkstations} onChange={setTotalWorkstations} min={0} />
            </div>

            <div className="h-px bg-stone" />

            <ToggleRow
              label="Mover disassembling at origin"
              description="Crew breaks down workstations before loading"
              value={moverDisassembling}
              onChange={setMoverDisassembling}
            />

            <ToggleRow
              label="Mover reassembling at destination"
              description="Crew rebuilds workstations after delivery"
              value={moverReassembling}
              onChange={setMoverReassembling}
            />
          </Card>

          {/* ── Workstation Sampling card ── */}
          <SamplingCard
            displayedModularItems={displayedModularItems}
            samplesMap={samplesMap}
            setSamplesMap={setSamplesMap}
            totalWorkstations={totalWorkstations}
            workstationsSampled={workstationsSampled}
            setWorkstationsSampled={setWorkstationsSampled}
            modularWorkstationItem={modularWorkstationItem}
          />

          {/* ── Equipment card ── */}
          <EquipmentCard
            equipmentItems={computedEquipment}
            defaultOpen={false}
          />

          {/* Summary */}
          {displayComponents.length > 0 && (
            <p className="text-[12px] text-slate text-center px-2">
              Quantities scaled: {workstationsSampled} sampled → {totalWorkstations} total workstations
            </p>
          )}
        </div>

        {/* Desktop layout — two independent columns */}
        <div className="hidden md:flex md:gap-3 md:items-start">
          {/* Left column: Settings + Equipment */}
          <div className="flex-1 space-y-3">
            <Card title="Modular Workstations">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-small font-medium text-midnight">Total Workstations</p>
                  <p className="text-[12px] text-slate mt-0.5">Number of workstations being moved</p>
                </div>
                <Counter value={totalWorkstations} onChange={setTotalWorkstations} min={0} />
              </div>

              <div className="h-px bg-stone" />

              <ToggleRow
                label="Mover disassembling at origin"
                description="Crew breaks down workstations before loading"
                value={moverDisassembling}
                onChange={setMoverDisassembling}
              />

              <ToggleRow
                label="Mover reassembling at destination"
                description="Crew rebuilds workstations after delivery"
                value={moverReassembling}
                onChange={setMoverReassembling}
              />
            </Card>

            <EquipmentCard
              equipmentItems={computedEquipment}
              defaultOpen
            />

            {displayComponents.length > 0 && (
              <p className="text-[12px] text-slate text-center px-2">
                Quantities scaled: {workstationsSampled} sampled → {totalWorkstations} total workstations
              </p>
            )}
          </div>

          {/* Right column: Sampling */}
          <div className="flex-1">
            <SamplingCard
              displayedModularItems={displayedModularItems}
              samplesMap={samplesMap}
              setSamplesMap={setSamplesMap}
              totalWorkstations={totalWorkstations}
              workstationsSampled={workstationsSampled}
              setWorkstationsSampled={setWorkstationsSampled}
              modularWorkstationItem={modularWorkstationItem}
            />
          </div>
        </div>
      </div>

    </div>
  );
}
